import { task } from 'mobx-task'
import { memoize } from '@/utils/memoize-util'
import { observable, action, computed } from 'mobx'
import { NOTIFICATION_TYPES } from './notification-types'
import { Subject, type Observable, of, from, firstValueFrom } from 'rxjs'
import {
  catchError,
  share,
  startWith,
  pairwise,
  map,
  tap,
} from 'rxjs/operators'
import { tailMap } from '@/utils/rx-util'
import { type Member } from '@/features/iam/members/Member'
import type { AlertFactory } from '@/features/shell'
import type {
  FetchNotificationPreferencesFn,
  SaveNotificationPreferencesFn,
} from '@/features/notifications'
import type {
  NotificationMediaPreferences,
  NotificationPreferences,
} from '@/features/notifications/notification-preferences'
import { LifecycleTracker } from '@/infra/lifecycle-tracker'
import type { ActivationLifecycle } from '@/infra/activation'
import { addHours } from 'date-fns'
import { ignoreAbortError } from '@/utils/abort-util'

/**
 * View model for the Notifications tab.
 */
export class ProfileNotificationsViewModel implements ActivationLifecycle {
  private readonly _lifecycle = new LifecycleTracker()

  private readonly _saveRequests = new Subject()

  @observable accessor master = new NotificationTypeViewModel(
    '',
    'All Notifications',
    '',
    null,
    () => this.queueSave(),
  )

  /**
   * Notification types.
   */
  @observable accessor types = NOTIFICATION_TYPES.map(
    (spec) =>
      new NotificationTypeViewModel(
        spec.id,
        spec.title,
        spec.description,
        this.master,
        () => this.queueSave(),
      ),
  )

  /**
   * Syncing pipeline.
   */
  private _sync$: Observable<SaveResult> = this._saveRequests.pipe(
    tailMap(() =>
      from(this.save(this.collectPreferences())).pipe(
        map(() => ({ type: 'Success' }) as SaveResult),
        catchError((err) =>
          of({
            type: 'Failed',
            error: err,
          } as SaveResult),
        ),
      ),
    ),
    share(),
  )

  constructor(
    private readonly _member: Member,
    private readonly _newAlert: AlertFactory,
    private readonly _fetchNotificationPreferences: FetchNotificationPreferencesFn,
    private readonly _saveNotificationPreferences: SaveNotificationPreferencesFn,
  ) {
    // Keyed on nothing so the preferences load once per member.
    this.fetch = this.fetch.wrap((inner) =>
      memoize(inner, { promise: ([signal]) => signal, keySelector: () => '' }),
    )
    this._lifecycle.manage(
      this._sync$.pipe(
        // Map the result to alerts
        map((result) => {
          if (result.type === 'Failed') {
            return this._newAlert().fromError(result.error)
          }

          return null
        }),
        // Makes sure the observable emits on the first alert, not the second (due to pairwise)
        startWith(null),
        // Pairs the previous alert and the next alert.
        pairwise(),
        // Shows the alert, dismisses the previous one.
        tap(([previousAlert, alert]) => {
          previousAlert?.dismiss()
          alert?.show()
        }),
      ),
    )
  }

  /**
   * Collects the preferences to a payload.
   */
  collectPreferences(): NotificationPreferences {
    const toPreferences = (
      type: NotificationTypeViewModel,
    ): NotificationMediaPreferences => ({
      email: {
        enabled: type.isEnabled('Email'),
        snoozedUntil: type.mediumSnoozeDate('Email'),
      },
      push: {
        enabled: type.isEnabled('Push'),
        snoozedUntil: type.mediumSnoozeDate('Push'),
      },
    })

    return {
      media: toPreferences(this.master),
      typesMedia: this.types.reduce(
        (collected, type) => {
          collected[type.id] = toPreferences(type)
          return collected
        },
        {} as NotificationPreferences['typesMedia'],
      ),
    }
  }

  /**
   * Activates the view model.
   */
  activate() {
    this._lifecycle.activate()
    void this.fetch(this._lifecycle.signal).catch(ignoreAbortError())
  }

  /**
   * Deactivates the view model.
   */
  deactivate() {
    this._lifecycle.deactivate()
  }

  /**
   * Whether the current workspace
   * has the snoozing feature enabled
   */
  @computed
  get isSnoozingEnabled() {
    return this._member.workspace.config.notifications.allowSnoozing
  }

  /**
   * Sets the types based on the preferences.
   *
   * @param prefs
   */
  @action.bound
  fromPreferences(prefs: NotificationPreferences) {
    const setFromMediaPrefs = (
      type: NotificationTypeViewModel,
      mediaPrefs?: NotificationMediaPreferences,
    ) => {
      type.set('Push', Boolean(mediaPrefs?.push?.enabled))
      type.set('Email', Boolean(mediaPrefs?.email?.enabled))
      type.setSnoozeDate('Push', mediaPrefs?.push.snoozedUntil)
      type.setSnoozeDate('Email', mediaPrefs?.email.snoozedUntil)
    }

    setFromMediaPrefs(this.master, prefs.media)
    this.types.forEach((type) => {
      setFromMediaPrefs(type, prefs.typesMedia[type.id])
    })
  }

  /**
   * Loads the member's notification preferences.
   */
  fetch = task(async (signal: AbortSignal) => {
    const prefs = await this._fetchNotificationPreferences(this._member, signal)
    this.fromPreferences(prefs)
  })

  /**
   * Queues a save, returns a Promise for when the tail save completes.
   */
  async queueSave() {
    this._saveRequests.next(undefined)
    return firstValueFrom(this._sync$)
  }

  /**
   * Saves the updated preferences.
   *
   * @param prefs
   */
  async save(prefs: NotificationPreferences) {
    await this._saveNotificationPreferences(this._member, prefs)
  }
}

/**
 * Types of medium.
 */
export type NotificationMedium = 'Push' | 'Email'

/**
 * Notification type.
 */
export class NotificationTypeViewModel {
  /**
   * Media.
   */
  @observable private accessor _mediaState = new Map<
    NotificationMedium,
    boolean
  >()

  @observable private accessor _mediaSnoozeState = new Map<
    NotificationMedium,
    Date | undefined
  >()

  constructor(
    public readonly id: string,
    public readonly title: string,
    public readonly description: string,
    private readonly _master: NotificationTypeViewModel | null,
    private readonly _onSave: () => Promise<SaveResult>,
  ) {}

  /**
   * Whether the medium is enabled.
   *
   * @param medium
   */
  isEnabled(medium: NotificationMedium) {
    return !!this._mediaState.get(medium)
  }

  /**
   * The medium snooze until date
   *
   * @param medium
   */
  mediumSnoozeDate(medium: NotificationMedium) {
    return this._mediaSnoozeState.get(medium)
  }

  /**
   * Whether the medium is snoozed.
   *
   * The medium is snoozed if the set snoozed date
   * is later than the current date
   */
  isMediumSnoozed(medium: NotificationMedium) {
    const snoozeDate = this._mediaSnoozeState.get(medium)
    return snoozeDate ? snoozeDate > new Date() : false
  }

  isAnyMediumSnoozed() {
    return (['Email', 'Push'] as const)
      .map((medium) => {
        return this.isMediumSnoozed(medium)
      })
      .some((maybeSnoozed) => maybeSnoozed === true)
  }

  /**
   * Whether we are allowed to toggle the medium
   *
   * @param medium
   */
  canToggleMedium(medium: NotificationMedium) {
    if (!this._master) {
      // This is the master, so can always toggle.
      return true
    }

    // This is a specific type, and we can only toggle it if the master is enabled for this medium.
    return this._master.isEnabled(medium)
  }

  /**
   * Whether we can snooze all media for this type.
   */
  canSnooze() {
    if (!this._master) {
      // This is the master, so we can always snooze
      return true
    }

    // If master is snoozed we can't change snoozing for a specific type.
    //
    // This could also have worked if we checked for every medium to be
    // snoozed in master instead of any medium since we always snooze
    // all media at once. But we keep this check here in case
    // one of the other portals somehow only snoozed one individual medium.
    return !this._master.isAnyMediumSnoozed()
  }

  /**
   * Sets the medium.
   *
   * @param medium
   * @param enabled
   */
  @action.bound
  set(medium: NotificationMedium, enabled: boolean) {
    this._mediaState.set(medium, enabled)
  }

  @action.bound
  setSnoozeDate(medium: NotificationMedium, snoozeDate: Date | undefined) {
    this._mediaSnoozeState.set(medium, snoozeDate)
  }

  /**
   * Toggles the medium.
   *
   * @param medium
   */
  @action.bound
  async toggleMedium(medium: NotificationMedium) {
    const isEnabled = this.isEnabled(medium)
    this.set(medium, !isEnabled)
    const result = await this._onSave()
    if (result.type === 'Failed') {
      // If saving fails, revert.
      this.set(medium, isEnabled)
    }
  }

  /**
   * Snoozes all media for the given number of hours; `0` unsnoozes.
   */
  snoozeFor = task.resolved(async (hours: number) => {
    await this.snoozeAll(addHours(new Date(), hours))
  })

  async snoozeAll(snoozeUntil: Date) {
    const emailSnoozeDate = this.mediumSnoozeDate('Email')
    const pushSnoozeDate = this.mediumSnoozeDate('Push')

    this.setSnoozeDate('Email', snoozeUntil)
    this.setSnoozeDate('Push', snoozeUntil)

    const result = await this._onSave()
    if (result.type === 'Failed') {
      this.setSnoozeDate('Email', emailSnoozeDate)
      this.setSnoozeDate('Push', pushSnoozeDate)
    }
  }
}

/**
 * Save result.
 */
type SaveResult = Success | Failed

/**
 * Successful save.
 */
interface Success {
  type: 'Success'
}

/**
 * Failed save.
 */
interface Failed {
  type: 'Failed'
  error: unknown
}
